import * as THREE from 'three';
import { getCar } from './data.js';
import { ArcadeVehicle } from './physics.js';

export const NETWORK_PROTOCOL_VERSION=22;
export const NETWORK_TICK_RATE=30;
export const NETWORK_FIXED_DT=1/NETWORK_TICK_RATE;
export const NETWORK_SNAPSHOT_RATE=20;
export const NETWORK_INTERPOLATION_DELAY_MS=100;
export const NETWORK_MAX_EXTRAPOLATION_MS=140;

const CAR_COLLIDERS=Object.freeze({
  muscle:{radius:.34086,length:1.058,wheelRadius:.1518,collisionWidth:.598},
  sport:{radius:.335616,length:1.0488,wheelRadius:.1334,collisionWidth:.5888},
  hotrod:{radius:.3128,length:1.0258,wheelRadius:.161,collisionWidth:.529},
  rally:{radius:.319884,length:.9798,wheelRadius:.1518,collisionWidth:.5612},
  pickup:{radius:.34086,length:1.0672,wheelRadius:.1702,collisionWidth:.598},
  van:{radius:.335616,length:.9752,wheelRadius:.1426,collisionWidth:.5888},
  'hyper3d-muscle-test':{radius:.358,length:1.095,wheelRadius:.108,collisionHeight:.42},
  'hyper3d-van-test':{radius:.339,length:1.019,wheelRadius:.098,collisionHeight:.478},
});

const clamp=(value,min,max)=>Math.min(max,Math.max(min,Number(value)||0));

export function normalizeNetworkCollider(value={},fallback=CAR_COLLIDERS.muscle){
  const base=fallback||CAR_COLLIDERS.muscle;
  const radius=clamp(value.radius??base.radius,.08,1.5);
  const length=clamp(value.length??base.length,.15,3);
  const collisionHeight=clamp(value.collisionHeight??base.collisionHeight??.82,.08,2.5);
  return {
    radius,
    length,
    wheelRadius:clamp(value.wheelRadius??base.wheelRadius,.03,.8),
    collisionWidth:clamp(value.collisionWidth??base.collisionWidth??radius*2,.1,2.5),
    collisionLength:clamp(value.collisionLength??base.collisionLength??length,.15,3),
    collisionHeight,
    collisionOffsetX:clamp(value.collisionOffsetX??base.collisionOffsetX??0,-1.5,1.5),
    collisionOffsetY:clamp(value.collisionOffsetY??base.collisionOffsetY??collisionHeight*.5,-1.5,2),
    collisionOffsetZ:clamp(value.collisionOffsetZ??base.collisionOffsetZ??0,-1.5,1.5),
  };
}

export function normalizeNetworkControls(value={}){
  return {
    throttle:clamp(value.throttle,-1,1),
    accelerator:clamp(value.accelerator??Math.max(0,Number(value.throttle)||0),0,1),
    steer:clamp(value.steer,-1,1),
    airPitch:clamp(value.airPitch,-1,1),
    airYaw:clamp(value.airYaw,-1,1),
    airRoll:clamp(value.airRoll,-1,1),
    jump:Boolean(value.jump),
    boost:Boolean(value.boost),
    stabilize:Boolean(value.stabilize),
    handbrake:Boolean(value.handbrake),
    fire:Boolean(value.fire),
    powerup:Boolean(value.powerup),
    aimX:clamp(value.aimX,-1,1),
    aimY:clamp(value.aimY,-1,1),
    aimZ:clamp(value.aimZ,-1,1),
  };
}

export function networkColliderFor(carId,override=null){
  const base=CAR_COLLIDERS[carId]||CAR_COLLIDERS.muscle;
  return normalizeNetworkCollider(override||base,base);
}

export function colliderDescriptorFromCar(car){
  return normalizeNetworkCollider(car?.userData||{});
}

export function applyNetworkCollider(car,carId,override=null){
  const existing=car.userData?.collisionWidth?colliderDescriptorFromCar(car):null;
  const collider=networkColliderFor(carId,override||existing);
  Object.assign(car.userData,collider);
  return collider;
}

export function createHeadlessNetworkVehicle(world,player,spawn){
  const car=new THREE.Object3D();
  const config=getCar(player.carId);
  const collider=networkColliderFor(player.carId,player.collider);
  const visual=new THREE.Object3D();
  car.userData={
    config,
    radius:collider.radius,
    length:collider.length,
    wheelRadius:collider.wheelRadius,
    collisionWidth:collider.collisionWidth,
    collisionLength:collider.collisionLength,
    collisionHeight:collider.collisionHeight,
    collisionOffsetX:collider.collisionOffsetX,
    collisionOffsetY:collider.collisionOffsetY,
    collisionOffsetZ:collider.collisionOffsetZ,
    wheels:[],
    flames:[],
    visual,
    shadow:{material:{opacity:0},scale:{set(){}}},
  };
  car.add(visual);
  return new ArcadeVehicle(car,world,{
    position:new THREE.Vector3(spawn.x,spawn.y,spawn.z),
    yaw:spawn.yaw,
  });
}

function blocksSpawn(obstacle,point,margin=.9){
  if(obstacle.minY>.84||obstacle.maxY<.02)return false;
  if(obstacle.shape==='circle'){
    const dx=point.x-obstacle.x;
    const dz=point.z-obstacle.z;
    const radius=obstacle.radius+margin;
    return dx*dx+dz*dz<radius*radius;
  }
  return point.x>obstacle.minX-margin&&point.x<obstacle.maxX+margin
    &&point.z>obstacle.minZ-margin&&point.z<obstacle.maxZ+margin;
}

function safeSpawn(world,preferred,index,total,{navigator=null,occupied=[]}={}){
  const margin=2.2;
  const candidate=new THREE.Vector3();
  for(let attempt=0;attempt<64;attempt+=1){
    if(attempt===0)candidate.set(preferred.x,.02,preferred.z);
    else{
      const angle=(index/Math.max(1,total))*Math.PI*2+attempt*2.399963;
      const ring=.15+(attempt%6)*.058;
      candidate.set(Math.cos(angle)*world.width*ring,.02,Math.sin(angle)*world.depth*ring);
    }
    candidate.x=THREE.MathUtils.clamp(candidate.x,-world.width/2+margin,world.width/2-margin);
    candidate.z=THREE.MathUtils.clamp(candidate.z,-world.depth/2+margin,world.depth/2-margin);
    const safe=navigator?.nearestWalkablePoint(candidate)||candidate;
    safe.y=.02;
    const separated=occupied.every((point)=>point.distanceToSquared(safe)>1.8*1.8);
    if(separated&&!world.obstacles.some((obstacle)=>blocksSpawn(obstacle,safe,.72)))return safe.clone();
  }
  let fallbackState=(index+1)*.61803398875%1;
  const fallback=navigator?.findRandomPoint(()=>{
    fallbackState=(fallbackState+.61803398875)%1;
    return fallbackState;
  })
    ||navigator?.nearestWalkablePoint(world.spawn)
    ||world.spawn.clone();
  fallback.y=.02;
  return fallback;
}

export function createNetworkSpawn(world,assignment,index,total,mode,options={}){
  let preferred;
  let yaw;
  if(mode==='battle'){
    const blue=assignment.teamId==='blue';
    const offsets=[-8,-4,0,4,8];
    preferred=new THREE.Vector3(
      offsets[assignment.spawnIndex]||0,
      .02,
      (blue?1:-1)*(world.depth/2-5.5),
    );
    yaw=blue?0:Math.PI;
  }else{
    const angle=(index/Math.max(1,total))*Math.PI*2+.42;
    preferred=new THREE.Vector3(Math.cos(angle)*world.width*.28,.02,Math.sin(angle)*world.depth*.28);
    yaw=Math.atan2(preferred.x,preferred.z);
  }
  const position=safeSpawn(world,preferred,index,total,options);
  return {x:position.x,y:position.y,z:position.z,yaw};
}

export function serializeNetworkVehicle(entry){
  const vehicle=entry.vehicle;
  return {
    id:entry.id,
    ack:entry.lastProcessedSeq,
    x:vehicle.car.position.x,
    y:vehicle.car.position.y,
    z:vehicle.car.position.z,
    vx:vehicle.velocity.x,
    vy:vehicle.velocity.y,
    vz:vehicle.velocity.z,
    yaw:vehicle.yaw,
    pitch:vehicle.pitch,
    roll:vehicle.roll,
    qx:vehicle.orientation.x,
    qy:vehicle.orientation.y,
    qz:vehicle.orientation.z,
    qw:vehicle.orientation.w,
    angularX:vehicle.angularVelocity.x,
    angularY:vehicle.angularVelocity.y,
    angularZ:vehicle.angularVelocity.z,
    grounded:vehicle.grounded,
    wheelsGrounded:vehicle.wheelsGrounded,
    uprightness:vehicle.uprightness,
    groundHeight:vehicle.groundHeight,
    nitro:vehicle.nitro,
    boosting:vehicle.boosting,
    forwardSpeed:vehicle.forwardSpeed,
    lateralSpeed:vehicle.lateralSpeed,
    skidAmount:vehicle.skidAmount,
    steer:vehicle.steer,
    throttle:vehicle.throttle,
    distance:vehicle.distance,
    topSpeedMultiplier:vehicle.topSpeedMultiplier,
    airTime:vehicle.airTime,
    lastAirTime:vehicle.lastAirTime,
    impactCooldown:vehicle.impactCooldown,
    impactPitchVelocity:vehicle.impactPitchVelocity,
    impactYawVelocity:vehicle.impactYawVelocity,
    impactRollVelocity:vehicle.impactRollVelocity,
    jumpBufferTimer:vehicle.jumpBufferTimer,
    groundedGraceTimer:vehicle.groundedGraceTimer,
    airDodgeAvailable:vehicle.airDodgeAvailable,
    airDodgeWindowTimer:vehicle.airDodgeWindowTimer,
    airDodgeSpinTimer:vehicle.airDodgeSpinTimer,
    airDodgeImpactTimer:vehicle.airDodgeImpactTimer,
    health:vehicle.health,
    disabled:vehicle.disabled,
    // `Infinity` es un estado interno válido para un eliminado definitivo,
    // pero JSON lo convierte en `null`. Lo hacemos explícito para que el
    // cliente nunca lo confunda con un temporizador vencido.
    respawnTimer:Number.isFinite(vehicle.respawnTimer)?vehicle.respawnTimer:null,
    invulnerableTimer:vehicle.invulnerableTimer,
    chaseInstability:vehicle.chaseInstability||0,
    chasePressureHold:vehicle.chasePressureHold||0,
    chaseHitCooldown:vehicle.chaseHitCooldown||0,
    chaseSlowTimer:vehicle.chaseSlowTimer||0,
    chaseNitroLockTimer:vehicle.chaseNitroLockTimer||0,
    powerupHeld:vehicle.powerupHeld||0,
    powerupPending:vehicle.powerupPending||0,
    powerupRevealTimer:vehicle.powerupRevealTimer||0,
    nitroOverchargeTimer:vehicle.nitroOverchargeTimer||0,
    powerupShieldTimer:vehicle.powerupShieldTimer||0,
    homingAmmoTimer:vehicle.homingAmmoTimer||0,
    ruptureFuses:entry.ruptureFuses||0,
    ruptureInstability:entry.ruptureInstability||0,
    ruptureRespawnTimer:entry.ruptureRespawnTimer||0,
    ruptureEliminated:Boolean(entry.ruptureEliminated),
    ruptureReachedPlatform:entry.ruptureReachedPlatform||0,
    aimX:Number(vehicle.aimDirection?.x)||0,
    aimY:Number(vehicle.aimDirection?.y)||0,
    aimZ:Number(vehicle.aimDirection?.z)||0,
  };
}

export function applyNetworkVehicleState(vehicle,state){
  vehicle.car.position.set(state.x,state.y,state.z);
  vehicle.velocity.set(state.vx,state.vy,state.vz);
  vehicle.yaw=state.yaw;
  vehicle.pitch=state.pitch;
  vehicle.roll=state.roll;
  if([state.qx,state.qy,state.qz,state.qw].every(Number.isFinite)){
    vehicle.setOrientationQuaternion(
      new THREE.Quaternion(state.qx,state.qy,state.qz,state.qw),
    );
  }else{
    vehicle.setOrientationEuler(state.pitch,state.yaw,state.roll);
  }
  vehicle.angularVelocity.set(
    Number(state.angularX)||0,
    Number(state.angularY)||0,
    Number(state.angularZ)||0,
  );
  vehicle.grounded=Boolean(state.grounded);
  vehicle.uprightness=Number.isFinite(state.uprightness)?state.uprightness:vehicle.uprightness;
  vehicle.wheelsGrounded=state.wheelsGrounded===undefined
    ?vehicle.grounded&&vehicle.uprightness>.5
    :Boolean(state.wheelsGrounded);
  vehicle.groundHeight=state.groundHeight;
  vehicle.nitro=state.nitro;
  vehicle.boosting=Boolean(state.boosting);
  if([state.aimX,state.aimY,state.aimZ].every(Number.isFinite)){
    if(!vehicle.aimDirection)vehicle.aimDirection=new THREE.Vector3();
    vehicle.aimDirection.set(state.aimX,state.aimY,state.aimZ);
    if(vehicle.aimDirection.lengthSq()>.000001)vehicle.aimDirection.normalize();
  }
  vehicle.forwardSpeed=state.forwardSpeed;
  vehicle.lateralSpeed=state.lateralSpeed;
  vehicle.skidAmount=state.skidAmount;
  vehicle.steer=state.steer;
  vehicle.throttle=state.throttle;
  vehicle.distance=state.distance;
  vehicle.topSpeedMultiplier=state.topSpeedMultiplier;
  vehicle.airTime=state.airTime;
  vehicle.lastAirTime=state.lastAirTime;
  vehicle.impactCooldown=state.impactCooldown;
  vehicle.impactPitchVelocity=state.impactPitchVelocity||0;
  vehicle.impactYawVelocity=state.impactYawVelocity||0;
  vehicle.impactRollVelocity=state.impactRollVelocity||0;
  vehicle.jumpBufferTimer=Math.max(0,Number(state.jumpBufferTimer)||0);
  vehicle.groundedGraceTimer=Math.max(0,Number(state.groundedGraceTimer)||0);
  vehicle.airDodgeAvailable=Boolean(state.airDodgeAvailable);
  vehicle.airDodgeWindowTimer=Math.max(0,Number(state.airDodgeWindowTimer)||0);
  vehicle.airDodgeSpinTimer=Math.max(0,Number(state.airDodgeSpinTimer)||0);
  vehicle.airDodgeImpactTimer=Math.max(0,Number(state.airDodgeImpactTimer)||0);
  vehicle.health=state.health;
  vehicle.disabled=Boolean(state.disabled);
  vehicle.respawnTimer=typeof state.respawnTimer==='number'&&Number.isFinite(state.respawnTimer)
    ?Math.max(0,state.respawnTimer)
    :vehicle.disabled?Infinity:0;
  vehicle.invulnerableTimer=state.invulnerableTimer;
  vehicle.chaseInstability=state.chaseInstability||0;
  vehicle.chasePressureHold=state.chasePressureHold||0;
  vehicle.chaseHitCooldown=state.chaseHitCooldown||0;
  vehicle.chaseSlowTimer=state.chaseSlowTimer||0;
  vehicle.chaseNitroLockTimer=state.chaseNitroLockTimer||0;
  vehicle.powerupHeld=state.powerupHeld||0;
  vehicle.powerupPending=state.powerupPending||0;
  vehicle.powerupRevealTimer=Math.max(0,state.powerupRevealTimer||0);
  vehicle.nitroOverchargeTimer=Math.max(0,state.nitroOverchargeTimer||0);
  vehicle.powerupShieldTimer=Math.max(0,state.powerupShieldTimer||0);
  vehicle.homingAmmoTimer=Math.max(0,state.homingAmmoTimer||0);
  vehicle.ruptureFuses=state.ruptureFuses||0;
  vehicle.ruptureInstability=state.ruptureInstability||0;
  vehicle.ruptureRespawnTimer=state.ruptureRespawnTimer||0;
  vehicle.ruptureEliminated=Boolean(state.ruptureEliminated);
  vehicle.ruptureReachedPlatform=state.ruptureReachedPlatform||0;
  vehicle.car.visible=!vehicle.disabled;
  vehicle.car.quaternion.copy(vehicle.orientation);
}

export function lerpNetworkAngle(from,to,alpha){
  const delta=Math.atan2(Math.sin(to-from),Math.cos(to-from));
  return from+delta*alpha;
}

export function interpolateNetworkState(first,second,alpha){
  const mix=(a,b)=>a+(b-a)*alpha;
  const aimDirection=new THREE.Vector3(
    mix(first.aimX||0,second.aimX||0),
    mix(first.aimY||0,second.aimY||0),
    mix(first.aimZ||0,second.aimZ||0),
  );
  if(aimDirection.lengthSq()>.000001)aimDirection.normalize();
  const firstQuaternion=new THREE.Quaternion(
    Number(first.qx)||0,
    Number(first.qy)||0,
    Number(first.qz)||0,
    Number.isFinite(first.qw)?first.qw:1,
  ).normalize();
  const secondQuaternion=new THREE.Quaternion(
    Number(second.qx)||0,
    Number(second.qy)||0,
    Number(second.qz)||0,
    Number.isFinite(second.qw)?second.qw:1,
  ).normalize();
  firstQuaternion.slerp(secondQuaternion,alpha);
  return {
    ...second,
    x:mix(first.x,second.x),y:mix(first.y,second.y),z:mix(first.z,second.z),
    vx:mix(first.vx,second.vx),vy:mix(first.vy,second.vy),vz:mix(first.vz,second.vz),
    yaw:lerpNetworkAngle(first.yaw,second.yaw,alpha),
    pitch:lerpNetworkAngle(first.pitch,second.pitch,alpha),
    roll:lerpNetworkAngle(first.roll,second.roll,alpha),
    qx:firstQuaternion.x,qy:firstQuaternion.y,qz:firstQuaternion.z,qw:firstQuaternion.w,
    angularX:mix(first.angularX||0,second.angularX||0),
    angularY:mix(first.angularY||0,second.angularY||0),
    angularZ:mix(first.angularZ||0,second.angularZ||0),
    aimX:aimDirection.x,
    aimY:aimDirection.y,
    aimZ:aimDirection.z,
    uprightness:mix(first.uprightness??1,second.uprightness??1),
    wheelsGrounded:Boolean(second.wheelsGrounded),
    nitro:mix(first.nitro,second.nitro),
    forwardSpeed:mix(first.forwardSpeed,second.forwardSpeed),
    lateralSpeed:mix(first.lateralSpeed,second.lateralSpeed),
    skidAmount:mix(first.skidAmount,second.skidAmount),
    steer:mix(first.steer,second.steer),
    throttle:mix(first.throttle,second.throttle),
    distance:mix(first.distance,second.distance),
    topSpeedMultiplier:mix(first.topSpeedMultiplier,second.topSpeedMultiplier),
    airTime:mix(first.airTime,second.airTime),
    lastAirTime:mix(first.lastAirTime,second.lastAirTime),
    impactCooldown:mix(first.impactCooldown,second.impactCooldown),
    impactPitchVelocity:mix(first.impactPitchVelocity||0,second.impactPitchVelocity||0),
    impactYawVelocity:mix(first.impactYawVelocity||0,second.impactYawVelocity||0),
    impactRollVelocity:mix(first.impactRollVelocity||0,second.impactRollVelocity||0),
    health:mix(first.health,second.health),
    respawnTimer:Number.isFinite(first.respawnTimer)&&Number.isFinite(second.respawnTimer)
      ?mix(first.respawnTimer,second.respawnTimer)
      :second.respawnTimer,
    invulnerableTimer:mix(first.invulnerableTimer,second.invulnerableTimer),
    chaseInstability:mix(first.chaseInstability,second.chaseInstability),
    chasePressureHold:mix(first.chasePressureHold,second.chasePressureHold),
    chaseHitCooldown:mix(first.chaseHitCooldown,second.chaseHitCooldown),
    chaseSlowTimer:mix(first.chaseSlowTimer,second.chaseSlowTimer),
    chaseNitroLockTimer:mix(first.chaseNitroLockTimer,second.chaseNitroLockTimer),
    powerupHeld:second.powerupHeld||0,
    powerupPending:second.powerupPending||0,
    powerupRevealTimer:mix(first.powerupRevealTimer||0,second.powerupRevealTimer||0),
    nitroOverchargeTimer:mix(first.nitroOverchargeTimer||0,second.nitroOverchargeTimer||0),
    powerupShieldTimer:mix(first.powerupShieldTimer||0,second.powerupShieldTimer||0),
    homingAmmoTimer:mix(first.homingAmmoTimer||0,second.homingAmmoTimer||0),
    ruptureFuses:second.ruptureFuses||0,
    ruptureInstability:mix(first.ruptureInstability||0,second.ruptureInstability||0),
    ruptureRespawnTimer:mix(first.ruptureRespawnTimer||0,second.ruptureRespawnTimer||0),
    ruptureEliminated:Boolean(second.ruptureEliminated),
    ruptureReachedPlatform:second.ruptureReachedPlatform||0,
  };
}

// Cuando el siguiente snapshot se demora unos milisegundos no congelamos el
// auto remoto en el último punto conocido: continuamos brevemente con su
// velocidad y rotación recientes. El límite corto evita adivinar demasiado si
// la conexión realmente se corta; el próximo snapshot corrige el resultado.
export function extrapolateNetworkState(previous,latest,seconds,sampleSeconds=NETWORK_FIXED_DT){
  if(latest.disabled)return latest;
  const time=clamp(seconds,0,NETWORK_MAX_EXTRAPOLATION_MS/1000);
  const interval=Math.max(.001,Number(sampleSeconds)||NETWORK_FIXED_DT);
  const angularRate=(from,to)=>Math.atan2(Math.sin(to-from),Math.cos(to-from))/interval;
  const result={
    ...latest,
    x:latest.x+latest.vx*time,
    y:latest.y+latest.vy*time,
    z:latest.z+latest.vz*time,
    yaw:latest.yaw+angularRate(previous?.yaw??latest.yaw,latest.yaw)*time,
    pitch:latest.pitch+angularRate(previous?.pitch??latest.pitch,latest.pitch)*time,
    roll:latest.roll+angularRate(previous?.roll??latest.roll,latest.roll)*time,
  };
  if([latest.qx,latest.qy,latest.qz,latest.qw].every(Number.isFinite)){
    const orientation=new THREE.Quaternion(latest.qx,latest.qy,latest.qz,latest.qw).normalize();
    const angular=new THREE.Vector3(
      (latest.angularX||0)+(latest.impactPitchVelocity||0),
      (latest.angularY||0)+(latest.impactYawVelocity||0),
      (latest.angularZ||0)+(latest.impactRollVelocity||0),
    );
    const angle=angular.length()*time;
    if(angle>.000001){
      const delta=new THREE.Quaternion().setFromAxisAngle(angular.normalize(),angle);
      orientation.multiply(delta).normalize();
    }
    result.qx=orientation.x;
    result.qy=orientation.y;
    result.qz=orientation.z;
    result.qw=orientation.w;
  }
  return result;
}
